import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  DARK,
  LIGHT,
  applyTheme,
  getTheme,
  isWidgetRoute,
  persistTheme,
} from "./themeStore";

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(getTheme);

  // Keep <html> and the cookie in sync whenever the theme changes.
  // Skip entirely on the embeddable widget route: that iframe manages its own
  // theme and must not inherit the app's cookie (the pre-paint script already
  // pinned it to light there).
  useEffect(() => {
    if (isWidgetRoute()) return;
    applyTheme(theme);
    persistTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next === LIGHT ? LIGHT : DARK);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === DARK ? LIGHT : DARK));
  }, []);

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme, isDark: theme === DARK }),
    [theme, setTheme, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
